import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { Package, ExternalLink } from 'lucide-react';
import { cpGpMatchClient } from '../../../infrastructure/CpGpMatchClient';
import { ROUTES } from '../../../routes/routes';

interface CpGpMatch {
  cpProductId: string;
  cpItemId: string;
  gpProductId: string;
  keyword?: string;
  thumbnailImageUrl?: string;
  productPageUrl?: string;
  cpItemName?: string;
  cpVendorItemId?: string;
}

const MatchedGpiPage: React.FC = () => {
  const { gpi } = useParams<{ gpi: string }>();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<CpGpMatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!gpi) {
        setError('No GP Product ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const result = await cpGpMatchClient.getMatchesForGpProduct(gpi);
        setMatches(result.matches || []);

        if (result.matches.length === 1) {
          const match = result.matches[0];
          navigate(ROUTES.sourcing.matchedDetail(match.cpProductId, match.cpItemId));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch matches');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();
  }, [gpi, navigate]);

  const handleSelectMatch = (match: CpGpMatch) => {
    navigate(ROUTES.sourcing.matchedDetail(match.cpProductId, match.cpItemId));
  };

  if (isLoading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading GP-CP matches...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (matches.length === 0) {
    return (
      <Container className="py-4">
        <Card>
          <Card.Body className="text-center py-5">
            <Package size={64} className="text-muted mb-3" />
            <h4>No Matches Found</h4>
            <p className="text-muted">No CP products matched with GP Product ID: {gpi}</p>
            <Button variant="outline-secondary" onClick={() => navigate(ROUTES.sourcing.matched)}>
              Go to Matched Products
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h4>GP-CP Matches for GP Product ID: {gpi}</h4>
        <p className="text-muted">Found {matches.length} matched product{matches.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="row g-3">
        {matches.map((match) => (
          <div key={`${match.cpProductId}-${match.cpItemId}`} className="col-12 col-md-6 col-lg-4">
            <Card className="h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => handleSelectMatch(match)}>
              <Card.Body>
                <div className="d-flex gap-3">
                  {match.thumbnailImageUrl ? (
                    <img
                      src={match.thumbnailImageUrl}
                      alt="Product"
                      style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                      onError={(e) => {
                        const target = e.currentTarget as HTMLImageElement;
                        target.src = 'https://via.placeholder.com/80?text=No+Image';
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                      }}
                    >
                      <Package size={32} className="text-muted" />
                    </div>
                  )}

                  <div className="flex-grow-1">
                    <div className="mb-2">
                      <Badge bg="primary" className="me-1">
                        CPPI: {match.cpProductId}
                      </Badge>
                      <Badge bg="info">CPII: {match.cpItemId}</Badge>
                    </div>

                    {match.cpItemName && (
                      <h6 className="mb-2" style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                        {match.cpItemName}
                      </h6>
                    )}

                    {match.cpVendorItemId && (
                      <small className="text-muted d-block mb-2">Vendor ID: {match.cpVendorItemId}</small>
                    )}

                    {match.keyword && <small className="text-muted d-block mb-2">Keyword: {match.keyword}</small>}

                    {match.productPageUrl && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(match.productPageUrl, '_blank');
                        }}
                      >
                        <ExternalLink size={14} className="me-1" />
                        View on Coupang
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
    </Container>
  );
};

export default MatchedGpiPage;

